import { isUpdateApplying, setUpdateActivity } from './update-safety'
let recordingInstance = 0
export type RecordingQuality = { duration: number; voicedSeconds: number; rms: number; peak: number; status: 'good' | 'short' | 'long' | 'quiet' | 'clipped' | 'unverified' }
export type RecordingProgress = { elapsedSeconds: number; level: number }
type RecordingPolicy = { minDuration: number; maxDuration: number; minVoicedSeconds?: number; rawInput?: boolean; maxOutputBytes?: number }
const shortPolicy: RecordingPolicy = { minDuration: 2, maxDuration: 4.05 }
export const longReferencePolicy: RecordingPolicy = { minDuration: 120, maxDuration: 300, minVoicedSeconds: 120, rawInput: true, maxOutputBytes: 64 * 1024 * 1024 }

export class VoiceRecorder {
  constructor(private readonly policy: RecordingPolicy = shortPolicy) {}
  private stream?: MediaStream
  private recorder?: MediaRecorder
  private chunks: Blob[] = []
  private startedAt = 0
  private recordingError?: Error
  private stopReject?: (error: Error) => void
  private timer?: ReturnType<typeof setInterval>
  private meterContext?: AudioContext
  private status: 'idle' | 'starting' | 'recording' | 'stopping' = 'idle'
  private generation = 0
  private updateActivity = `browser-recording-${++recordingInstance}`

  async start(onFailure?: (error: Error) => void, onProgress?: (progress: RecordingProgress) => void) {
    if (isUpdateApplying()) throw new Error('앱 업데이트 적용 중이에요. 앱이 다시 열리면 녹음해 주세요.')
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) throw new Error('unsupported')
    if (this.status !== 'idle') throw new Error('recorder-busy')
    this.status = 'starting'
    setUpdateActivity(this.updateActivity, true)
    const generation = ++this.generation
    let stream: MediaStream | undefined
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: !this.policy.rawInput, noiseSuppression: !this.policy.rawInput, autoGainControl: !this.policy.rawInput, channelCount: { ideal: 1 } }, video: false })
      if (this.status !== 'starting' || generation !== this.generation) { stream.getTracks().forEach(track => track.stop()); throw new Error('recorder-cancelled') }
      const track = stream.getAudioTracks()[0]
      if (!track || track.readyState !== 'live') throw new Error('microphone-disconnected')
      this.stream = stream
      this.chunks = []
      this.recordingError = undefined
      this.stopReject = undefined
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type))
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      this.recorder = recorder
      const fail = (error: Error) => {
        if (generation !== this.generation || this.status === 'idle') return
        this.recordingError = error
        const rejectStop = this.stopReject
        this.stopReject = undefined
        if (rejectStop) rejectStop(error)
        else {
          this.status = 'idle'
          setUpdateActivity(this.updateActivity, false)
          this.generation += 1
          this.releaseRecorder(recorder)
          this.cleanStream()
          this.chunks = []
        }
        onFailure?.(error)
      }
      track.onended = () => fail(new Error('microphone-disconnected'))
      let capturedBytes = 0
      recorder.ondataavailable = event => {
        if (generation !== this.generation || !event.data.size) return
        capturedBytes += event.data.size
        if (this.policy.maxOutputBytes && capturedBytes > this.policy.maxOutputBytes) { fail(new Error('recording-too-large')); return }
        this.chunks.push(event.data)
      }
      recorder.onerror = () => fail(new Error('media-recorder-error'))
      recorder.onstop = () => fail(new Error('recording-interrupted'))
      this.startedAt = Date.now()
      recorder.start(200)
      this.status = 'recording'
      if (onProgress) this.startMeter(stream, generation, onProgress)
    } catch (error) {
      stream?.getTracks().forEach(track => track.stop())
      if (generation === this.generation) {
        this.cleanStream()
        this.recorder = undefined
        this.status = 'idle'
        setUpdateActivity(this.updateActivity, false)
      }
      throw error
    }
  }

  stop() {
    return new Promise<{ blob: Blob; quality: RecordingQuality }>((resolve, reject) => {
      const recorder = this.recorder
      const generation = this.generation
      if (this.status !== 'recording' || !recorder || recorder.state === 'inactive') {
        this.cleanStream()
        if (this.status === 'idle') setUpdateActivity(this.updateActivity, false)
        return reject(this.recordingError ?? new Error('not-recording'))
      }
      this.status = 'stopping'
      let settled = false
      const fail = (error: Error) => {
        if (settled) return
        settled = true
        clearTimeout(timeout)
        this.stopReject = undefined
        this.status = 'idle'
        setUpdateActivity(this.updateActivity, false)
        this.recordingError = error
        this.generation += 1
        this.releaseRecorder(recorder)
        this.cleanStream()
        this.chunks = []
        reject(error)
      }
      const timeout = setTimeout(() => fail(new Error('recording-stop-timeout')), this.policy.minDuration >= 120 ? 60000 : 12000)
      this.stopReject = fail
      recorder.onstop = async () => {
        if (settled || generation !== this.generation) return
        const original = new Blob(this.chunks, { type: recorder.mimeType || this.chunks[0]?.type || 'audio/webm' })
        this.cleanStream()
        this.chunks = []
        try {
          if (!original.size) throw new Error('recording-empty')
          // PCM WAV has a measured duration without browser codec parameters.
          // MediaRecorder WebM commonly omits container duration metadata.
          const result = await prepareRecording(original, this.policy)
          if (settled || generation !== this.generation) return
          settled = true
          clearTimeout(timeout)
          this.stopReject = undefined
          this.recorder = undefined
          this.status = 'idle'
          setUpdateActivity(this.updateActivity, false)
          resolve(result)
        } catch (error) { fail(error instanceof Error ? error : new Error('recording-analysis-error')) }
      }
      try { recorder.stop() } catch (error) { fail(error instanceof Error ? error : new Error('media-recorder-stop-error')) }
    })
  }

  cancel() {
    this.stopReject?.(new Error('recorder-cancelled'))
    this.generation += 1
    this.releaseRecorder()
    this.cleanStream()
    this.chunks = []
    this.status = 'idle'
    setUpdateActivity(this.updateActivity, false)
  }

  private startMeter(stream: MediaStream, generation: number, onProgress: (progress: RecordingProgress) => void) {
    let analyser: AnalyserNode | undefined
    let values: Float32Array<ArrayBuffer> | undefined
    try {
      const context = new AudioContext()
      this.meterContext = context
      analyser = context.createAnalyser()
      analyser.fftSize = 512
      context.createMediaStreamSource(stream).connect(analyser)
      values = new Float32Array(analyser.fftSize)
      void context.resume().catch(() => undefined)
    } catch { /* Keep the elapsed timer even if metering is unavailable. */ }
    this.timer = setInterval(() => {
      if (generation !== this.generation || this.status !== 'recording') return
      let level = 0
      if (analyser && values) {
        try {
          analyser.getFloatTimeDomainData(values)
          const measured = Math.sqrt(values.reduce((sum, value) => sum + value * value, 0) / values.length)
          if (Number.isFinite(measured)) level = measured
        } catch { /* A closed meter must not interrupt microphone capture. */ }
      }
      onProgress({ elapsedSeconds: (Date.now() - this.startedAt) / 1000, level })
    }, 100)
  }

  private releaseRecorder(recorder = this.recorder) {
    if (!recorder) return
    recorder.onstop = null
    recorder.onerror = null
    recorder.ondataavailable = null
    if (this.recorder === recorder) this.recorder = undefined
    try { if (recorder.state !== 'inactive') recorder.stop() } catch { /* Device already closed. */ }
  }

  private cleanStream() {
    if (this.timer) clearInterval(this.timer)
    this.timer = undefined
    const context = this.meterContext
    this.meterContext = undefined
    if (context && context.state !== 'closed') void context.close().catch(() => undefined)
    this.stream?.getTracks().forEach(track => { track.onended = null; track.stop() })
    this.stream = undefined
  }
}

export async function recordingForUpload(original: Blob): Promise<Blob> {
  const type = original.type.split(';')[0].trim().toLowerCase()
  if (type !== 'audio/webm' && type !== 'audio/ogg') return original.slice(0, original.size, type)
  try { return (await prepareRecording(original)).blob }
  catch { throw new Error('기기에 저장된 녹음 형식을 읽지 못했어요. 해당 문장을 다시 녹음해 주세요.') }
}

export async function prepareLongReference(original: Blob): Promise<{ blob: Blob; quality: RecordingQuality }> {
  if (!original.size || original.size > 64 * 1024 * 1024) throw new Error('음성 파일은 64MB 이하로 준비해 주세요.')
  if (!['', 'audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/aac', 'audio/webm', 'audio/ogg'].includes(original.type.split(';')[0].toLowerCase())) throw new Error('WAV, MP3, M4A, WebM 또는 OGG 음성 파일을 선택해 주세요.')
  const duration = await importedAudioDuration(original)
  if (duration > longReferencePolicy.maxDuration) throw new Error('음성 파일이 5분을 넘어요. 2~5분 분량으로 다시 준비해 주세요.')
  return prepareRecording(original, longReferencePolicy)
}

async function importedAudioDuration(original: Blob): Promise<number> {
  const url = URL.createObjectURL(original)
  const audio = new Audio()
  audio.preload = 'metadata'
  try {
    return await new Promise<number>((resolve, reject) => {
      let settled = false
      const finish = (duration?: number) => {
        if (settled) return
        settled = true
        window.clearTimeout(timeout)
        if (duration && Number.isFinite(duration)) resolve(duration)
        else reject(new Error('음성 파일의 재생 길이를 확인하지 못했어요. 길이를 확인할 수 있는 WAV, MP3 또는 M4A 파일을 사용해 주세요.'))
      }
      const timeout = window.setTimeout(() => finish(), 10000)
      audio.onloadedmetadata = () => finish(audio.duration)
      audio.onerror = () => finish()
      audio.src = url
      audio.load()
    })
  } finally {
    audio.onloadedmetadata = null
    audio.onerror = null
    audio.removeAttribute('src')
    audio.load()
    URL.revokeObjectURL(url)
  }
}

const prepareRecording = async (original: Blob, policy: RecordingPolicy = shortPolicy): Promise<{ blob: Blob; quality: RecordingQuality }> => {
  const context = new AudioContext()
  try {
    const buffer = await context.decodeAudioData(await original.arrayBuffer())
    if (!buffer.length || !Number.isFinite(buffer.duration)) throw new Error('recording-empty')
    if (policy.maxOutputBytes && 44 + buffer.length * 2 > policy.maxOutputBytes) throw new Error('녹음 크기가 64MB를 넘어요. 더 짧게 녹음하거나 낮은 샘플레이트의 파일을 사용해 주세요.')
    const samples = new Float32Array(buffer.length)
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const input = buffer.getChannelData(channel)
      for (let index = 0; index < samples.length; index++) samples[index] += input[index] / buffer.numberOfChannels
    }
    let sum = 0, peak = 0, voicedSeconds = 0
    const windowSamples = Math.max(1, Math.round(buffer.sampleRate / 4))
    let windowSum = 0, windowCount = 0
    for (const value of samples) {
      const squared = value * value
      sum += squared; windowSum += squared; windowCount++
      peak = Math.max(peak, Math.abs(value))
      if (windowCount === windowSamples) {
        if (Math.sqrt(windowSum / windowCount) >= 0.008) voicedSeconds += windowCount / buffer.sampleRate
        windowSum = 0; windowCount = 0
      }
    }
    if (windowCount && Math.sqrt(windowSum / windowCount) >= 0.008) voicedSeconds += windowCount / buffer.sampleRate
    const rms = Math.sqrt(sum / samples.length)
    const duration = buffer.duration
    const status = duration < policy.minDuration ? 'short' : duration > policy.maxDuration ? 'long' : rms < 0.008 || (policy.minVoicedSeconds != null && voicedSeconds < policy.minVoicedSeconds) ? 'quiet' : peak > 0.995 ? 'clipped' : 'good'
    const bytes = new ArrayBuffer(44 + samples.length * 2)
    const view = new DataView(bytes)
    const text = (offset: number, value: string) => { for (let index = 0; index < value.length; index++) view.setUint8(offset + index, value.charCodeAt(index)) }
    text(0, 'RIFF'); view.setUint32(4, bytes.byteLength - 8, true); text(8, 'WAVE'); text(12, 'fmt ')
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true)
    view.setUint32(24, buffer.sampleRate, true); view.setUint32(28, buffer.sampleRate * 2, true)
    view.setUint16(32, 2, true); view.setUint16(34, 16, true); text(36, 'data'); view.setUint32(40, samples.length * 2, true)
    for (let index = 0; index < samples.length; index++) { const sample = Math.max(-1, Math.min(1, samples[index])); view.setInt16(44 + index * 2, Math.round(sample * (sample < 0 ? 32768 : 32767)), true) }
    return { blob: new Blob([bytes], { type: 'audio/wav' }), quality: { duration, voicedSeconds, rms, peak, status } }
  } finally { if (context.state !== 'closed') await context.close().catch(() => undefined) }
}
